import Vue from 'vue'
import Vuex from 'vuex'
import firebaseService from '@/services/firebase.service'
import router from '@/router'
import moduleEbooks from '@/services/ebooks.store'
import moduleError from '@/services/error.store'
import moduleFooter from '@/services/footer.store'
import moduleList from '@/services/lists.store'
import moduleLoader from '@/services/loader.store'
import moduleModal from '@/services/modal.store'
import modulePdf from '@/services/pdfs.store'
import $http from '@/services/http-common'
import { Toast } from 'buefy/dist/components/toast'

Vue.use(Vuex)

export default new Vuex.Store({
  modules: {
    moduleError,
    moduleEbooks,
    moduleFooter,
    moduleList,
    moduleLoader,
    moduleModal,
    modulePdf
  },
  state: {
    user: null
  },
  mutations: {
    setUser (state, payload) {
      state.user = payload
    }
  },
  actions: {
    async autoSignIn ({ commit }, payload) {
      try {
        const { data } = await $http.get(`/api/users/${payload.firebaseUser.uid}`)
        commit('setUser', data)
        return data
      } catch (error) {
        console.error(error)
        commit('setError', error.message)
      }
    },
    async getFreeEbooks ({ commit }) {
      try {
        commit('setLocalLoading', {
          active: true,
          componentId: 'publicEbooksList'
        })
        const idToken = await firebaseService.getToken()
        const headers = {}
        if (idToken) {
          headers.idToken = idToken
        }
        const { data } = await $http.get('/api/ebooks?type=free', {
          headers
        })
        commit('setLocalLoading', {
          active: false,
          componentId: 'publicEbooksList'
        })
        commit('setEbooks', data)
      } catch (error) {
        console.error(error)
        commit('setError', error.message)
        commit('setLocalLoading', {
          active: false,
          componentId: 'publicEbooksList'
        })
      }
    },
    async getUserEbooks ({ commit }) {
      try {
        commit('setLocalLoading', {
          active: true,
          componentId: 'userEbooksList'
        })
        const idToken = await firebaseService.getToken()
        const uid = await firebaseService.getUID()
        const { data } = await $http.get(`/api/users/${uid}/ebooks`, {
          headers: {
            idToken
          }
        })
        commit('setUserEbooks', data)
        commit('setLocalLoading', {
          active: false,
          componentId: 'userEbooksList'
        })
      } catch (error) {
        console.error(error)
        commit('setError', error.message)
        commit('setLocalLoading', {
          active: false,
          componentId: 'userEbooksList'
        })
      }
    },
    async handleUserSignInWithGoogleAuth ({ commit, dispatch }, payload) {
      try {
        commit('setFullPageLoading', true)
        if (payload.isNewUser) {
          const { data } = await $http.post('/signup', {
            email: payload.user.email,
            uid: payload.user.uid
          })
          commit('setUser', data)
        } else {
          dispatch('autoSignIn', { firebaseUser: payload.user })
        }
        commit('setFullPageLoading', false)
        router.replace('/')
      } catch (error) {
        commit('setFullPageLoading', false)
        console.error(error)
        commit('setError', error.message)
      }
    },
    async reSendChapter ({ commit }, payload) {
      try {
        commit('setFullPageLoading', true)
        await $http.post(`/api/users/${payload.user}/ebooks/${payload.ebookId}/chapters/${payload.sectionId}`)
        Toast.open({
          message: 'You\'ll receive an email shortly!',
          type: 'is-success'
        })
        commit('setError', null)
        commit('setFullPageLoading', false)
      } catch (error) {
        console.error(error)
        commit('setError', error.message)
        commit('setFullPageLoading', false)
      }
    },
    async reSendChapterFromEbookModal ({ commit }, payload) {
      try {
        commit('setLocalLoading', {
          active: true,
          componentId: 'continueReadingEbookModal'
        })
        await $http.post(`/api/users/${payload.user}/ebooks/${payload.ebookId}/chapters/${payload.sectionId}`)
        Toast.open({
          message: 'You\'ll receive an email shortly!',
          type: 'is-success'
        })
        commit('setError', null)
        commit('setLocalLoading', {
          active: false,
          componentId: 'continueReadingEbookModal'
        })
        commit('setStartEbookModal', false)
      } catch (error) {
        console.error(error)
        commit('setError', error.message)
        commit('setLocalLoading', {
          active: false,
          componentId: 'continueReadingEbookModal'
        })
      }
    },
    async reSendChapterFromEbookPage ({ commit }, payload) {
      try {
        commit('setLocalLoading', {
          active: true,
          componentId: 'continueEbookButton'
        })
        await $http.post(`/api/users/${payload.user}/ebooks/${payload.ebookId}/chapters/${payload.sectionId}`)
        Toast.open({
          message: 'You\'ll receive an email shortly!',
          type: 'is-success'
        })
        commit('setError', null)
        commit('setLocalLoading', {
          active: false,
          componentId: 'continueEbookButton'
        })
      } catch (error) {
        console.error(error)
        commit('setError', error.message)
        commit('setLocalLoading', {
          active: false,
          componentId: 'continueEbookButton'
        })
      }
    },
    async sendNextChapter ({ commit }, payload) {
      try {
        commit('setFullPageLoading', true)
        const { data } = await $http.post(`/api/users/${payload.user}/ebooks/${payload.ebookId}/chapters/${payload.sectionId}`)
        commit('setEbook', { ...data.ebook, ...data.nextSection })
        commit('setError', null)
        router.replace('/send')
        commit('setFullPageLoading', false)
      } catch (error) {
        console.error(error)
        commit('setError', error.message)
        commit('setFullPageLoading', false)
      }
    },
    async startReadEbook ({ commit }, payload) {
      try {
        commit('setLocalLoading', {
          active: true,
          componentId: 'publicEbooksList'
        })
        commit('setEbookFromEbooksToIsRead', { ebookId: payload.ebookId })
        const idToken = await firebaseService.getToken()
        const { data } = await $http.post(`/api/ebooks/${payload.ebookId}`, {}, {
          headers: {
            idToken
          }
        })
        Toast.open({
          message: 'You\'ll receive an email shortly!',
          type: 'is-success'
        })
        commit('setUser', data.user)
        commit('setError', null)
        commit('setLocalLoading', {
          active: false,
          componentId: 'publicEbooksList'
        })
      } catch (error) {
        console.error(error)
        commit('setError', error.message)
        commit('setLocalLoading', {
          active: false,
          componentId: 'publicEbooksList'
        })
      }
    },
    async startReadEbookFromEbookPage ({ commit }, payload) {
      try {
        commit('setLocalLoading', {
          active: true,
          componentId: 'startEbookButton'
        })

        const idToken = await firebaseService.getToken()
        const { data } = await $http.post(`/api/ebooks/${payload.id}`, {}, {
          headers: {
            idToken
          }
        })
        Toast.open({
          message: 'You\'ll receive an email shortly!',
          type: 'is-success'
        })
        commit('setUser', data.user)

        // set ebook section id and isRead
        const { isRead, sectionId, ...rest } = payload
        commit('setEbook', {
          ...rest,
          isRead: true,
          sectionId: data.section.id
        })

        commit('setError', null)
        commit('setLocalLoading', {
          active: false,
          componentId: 'startEbookButton'
        })
      } catch (error) {
        console.error(error)
        commit('setError', error.message)
        commit('setLocalLoading', {
          active: false,
          componentId: 'startEbookButton'
        })
      }
    },
    async startReadDiscoverEbook ({ commit }, payload) {
      try {
        commit('setLocalLoading', {
          active: true,
          componentId: 'startEbookModal'
        })
        commit('setEbookFromEbooksListToIsRead', { ebookId: payload.ebookId })
        const idToken = await firebaseService.getToken()
        const { data } = await $http.post(`/api/ebooks/${payload.ebookId}`, {}, {
          headers: {
            idToken
          }
        })
        Toast.open({
          message: 'You\'ll receive an email shortly!',
          type: 'is-success'
        })
        commit('setUser', data.user)
        commit('setEbookFromEbooksListSectionId', {
          ebookId: payload.ebookId,
          sectionId: data.section.id
        })
        commit('setError', null)
        commit('setLocalLoading', {
          active: false,
          componentId: 'startEbookModal'
        })
        commit('setStartEbookModal', false)
      } catch (error) {
        console.error(error)
        commit('setError', error.message)
        commit('setLocalLoading', {
          active: false,
          componentId: 'startEbookModal'
        })
      }
    },
    async subscribeUser ({ commit }, payload) {
      try {
        commit('setLocalLoading', {
          active: true,
          componentId: 'subscription'
        })
        const idToken = await firebaseService.getToken()
        const uid = await firebaseService.getUID()
        const { data } = await $http.put(`/api/users/${uid}/subscribe`, {
          coupon: payload.coupon,
          plan: payload.plan,
          token: payload.token
        }, {
          headers: {
            idToken
          }
        })
        Toast.open({
          message: 'Subscription successful!',
          type: 'is-success'
        })
        commit('setUser', data)
        commit('setLocalLoading', {
          active: false,
          componentId: 'subscription'
        })
        commit('setError', null)
        return true
      } catch (error) {
        console.error(error)
        commit('setError', error.message)
        commit('setLocalLoading', {
          active: false,
          componentId: 'subscription'
        })
        return false
      }
    },
    async updateSubscribptionToNewsLetter ({ commit, state }, payload) {
      try {
        const idToken = await firebaseService.getToken()
        const uid = await firebaseService.getUID()
        const user = state.user
        await $http.put(`/api/users/${uid}`, {
          isSubscribedToNewsletter: payload
        }, {
          headers: {
            idToken
          }
        })
        user.isSubscribedToNewsletter = payload
        commit('setUser', user)
      } catch (error) {
        console.error(error)
        commit('setError', error.message)
      }
    },
    async userSignIn ({ commit }, payload) {
      commit('setLocalLoading', {
        active: true,
        componentId: 'signin'
      })
      try {
        const firebaseUser = await firebaseService.signInWithEmailAndPassword(payload.email, payload.password)
        const { data } = await $http.get(`/api/users/${firebaseUser.user.uid}`)
        commit('setUser', data)
        commit('setLocalLoading', {
          active: false,
          componentId: 'signin'
        })
        commit('setError', null)
        router.replace('/')
      } catch (error) {
        console.error(error)
        commit('setError', error.message)
        commit('setLocalLoading', {
          active: false,
          componentId: 'signin'
        })
      }
    },
    async userSignUp ({ commit }, payload) {
      try {
        commit('setLocalLoading', {
          active: true,
          componentId: 'signup'
        })
        const firebaseUser = await firebaseService.createUserWithEmailAndPassword(payload.email, payload.password)
        if (firebaseUser && firebaseUser.user && !firebaseUser.user.emailVerified) {
          firebaseUser.user.sendEmailVerification()
        }
        const { data } = await $http.post('/signup', {
          email: payload.email,
          uid: firebaseUser.user.uid
        })
        commit('setUser', data)
        commit('setLocalLoading', {
          active: false,
          componentId: 'signup'
        })
        router.replace('/')
      } catch (error) {
        console.error(error)
        commit('setError', error.message)
        commit('setLocalLoading', {
          active: false,
          componentId: 'signup'
        })
      }
    },
    async userSignOut ({ commit }) {
      try {
        await firebaseService.signOut()
        commit('setUser', null)
        router.replace('/signin')
      } catch (error) {
        console.error(error)
        commit('setLocalLoading', {
          active: false,
          componentId: 'signup'
        })
      }
    },
    async uploadEbook ({ commit }, payload) {
      try {
        commit('setLocalLoading', {
          active: true,
          componentId: 'uploadEbook'
        })
        commit('setError', null)
        if (payload.file) {
          const idToken = await firebaseService.getToken()
          const formData = new FormData()
          formData.append('ebook', payload.file, payload.file.name)
          const { data } = await $http.post('api/ebooks', formData, {
            headers: {
              idToken
            }
          })
          commit('setUser', data)
          Toast.open({
            message: 'You\'ll receive an email quickly!',
            type: 'is-success'
          })
        } else {
          commit('setError', 'You need to upload a file first!')
        }
        commit('setLocalLoading', {
          active: false,
          componentId: 'uploadEbook'
        })
      } catch (error) {
        console.error(error)
        commit('setLocalLoading', {
          active: false,
          componentId: 'uploadEbook'
        })
      }
    }
  },
  getters: {
    getPlayOrder (state) {
      return state.playOrder
    },
    getUserCardLast4Digit (state) {
      if (state.user) {
        return state.user.cardLast4Digit
      }
      return null
    },
    getUserCustomerId (state) {
      if (state.user) {
        return state.user.customerId
      }
      return null
    },
    getUserTicketsCount (state) {
      if (state.user) {
        return state.user.bookmanTicketsCount
      }
      return 0
    },
    isAuthenticated (state) {
      return state.user
    },
    isErroneous (state) {
      return state.moduleError.error
    },
    isSubscribedToNewsletter (state) {
      if (state.user) {
        return state.user.isSubscribedToNewsletter
      }
      return false
    },
    isSubscribedUser (state) {
      if (state.user) {
        return state.user.subscriptionId != null
      }
      return false
    }
  }
})
